import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Select, Space, Tag, Tooltip } from 'antd';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { API } from '@/services/api.service';
import ColumnTitle from './ColumnTitle';
import TabTable from './TabTable';
import { date, errorText, money } from './format';
import { paramsOf } from './listParams';

/**
 * The accounts seen on imported statements and the project each belongs to,
 * a page at a time from the server. A return counts as „no statement" while
 * its project has no account here whose statements reach the due date.
 */
/** „87–94", „13", „30–32, 40": the missing numbers as ranges. */
const rangesOf = (numbers) => {
  const parts = [];
  for (let i = 0; i < numbers.length; i += 1) {
    let j = i;
    while (j + 1 < numbers.length && numbers[j + 1] === numbers[j] + 1) j += 1;
    parts.push(j > i ? `${numbers[i]}–${numbers[j]}` : String(numbers[i]));
    i = j;
  }
  return parts.join(', ');
};

export default function AccountsPanel({ company, projects, canManage }) {
  const { t } = useTranslation('taxReturns');
  const queryClient = useQueryClient();
  const { queryParams } = useQueryParamsContext();
  const params = useMemo(() => paramsOf('accounts', queryParams), [queryParams]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tax-returns-accounts', company, params],
    queryFn: () => API.taxReturns.getBankAccountsPage(company, params),
    enabled: Boolean(company),
    placeholderData: (prev) => prev,
  });

  const mutation = useMutation({
    mutationFn: ({ account, project }) => API.taxReturns.setBankAccountProject(company, account, project),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax-returns-accounts', company] });
      queryClient.invalidateQueries({ queryKey: ['tax-returns', company] });
    },
  });

  const columns = [
    {
      title: () => <ColumnTitle title={t('accounts.account')} sort="account" search />,
      dataIndex: 'accountFormatted',
      width: 240,
    },
    { title: () => <ColumnTitle title={t('accounts.bank')} sort="bank" />, dataIndex: 'bank', width: 130 },
    { title: t('accounts.currency'), dataIndex: 'currency', width: 90 },
    {
      title: t('fields.project'),
      dataIndex: 'project',
      width: 270,
      render: (p, row) =>
        canManage ? (
          <Select
            allowClear
            style={{ width: 240 }}
            value={p?._id || null}
            placeholder={t('filters.noProject')}
            loading={mutation.isPending && mutation.variables?.account === row.account}
            onClick={(e) => e.stopPropagation()}
            onChange={(value) => mutation.mutate({ account: row.account, project: value || null })}
            options={projects.map((x) => ({ value: x._id, label: `${x.code} ${x.name}` }))}
          />
        ) : p ? (
          `${p.code} ${p.name}`
        ) : (
          '—'
        ),
    },
    { title: t('accounts.first'), dataIndex: 'first', render: date, width: 120 },
    { title: () => <ColumnTitle title={t('accounts.latest')} sort="latest" />, dataIndex: 'latest', render: date, width: 140 },
    {
      title: () => <ColumnTitle title={t('accounts.statements')} sort="statements" />,
      dataIndex: 'statements',
      align: 'right',
      width: 120,
    },
    {
      title: () => <ColumnTitle title={t('accounts.gaps')} sort="gaps" />,
      dataIndex: 'gaps',
      width: 220,
      render: (gaps) =>
        gaps?.length ? (
          <Space wrap size={4}>
            {gaps.map((g) => (
              <Tooltip
                key={`${g.year}-${g.after.number}`}
                title={t('accounts.gapTooltip', {
                  after: `#${g.after.number}`,
                  afterDate: date(g.after.date),
                  before: `#${g.before.number}`,
                  beforeDate: date(g.before.date),
                  difference: g.difference === null ? '—' : money(g.difference),
                })}
              >
                <Tag color="red">
                  {g.missing.length
                    ? t('accounts.missing', { numbers: rangesOf(g.missing) })
                    : t('accounts.balanceBreak', { after: `#${g.after.number}`, before: `#${g.before.number}` })}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <p className="text-sm opacity-70">{t('accounts.help')}</p>
      {(isError || mutation.isError) && <Alert type="error" showIcon message={errorText(t, error || mutation.error)} />}
      {data?.withGaps > 0 && <Alert type="warning" showIcon message={t('accounts.gapsAlert', { count: data.withGaps })} />}
      <TabTable
        tab="accounts"
        title={t('tabs.accounts')}
        data={data?.items || []}
        total={data?.count || 0}
        status={isLoading ? 'pending' : isError ? 'error' : 'success'}
        columns={columns}
        projects={projects}
        rowKey="account"
      />
    </Space>
  );
}
